/**
 * Speech playback controller (Aug 29 2026 — Keith: "when an avatar is
 * talking there is a simple button that says 'pause/stop' … which allows
 * the user to pause or stop the avatar from talking").
 *
 * One page-level registry of "the utterance currently being spoken", so a
 * single zero-config UI control (`SpeechPlaybackControl` on the call
 * surface) can pause/resume/stop whatever is talking without knowing which
 * speech path produced it. The SDK has three audible paths and each one
 * registers here when it starts and marks itself ended when it finishes:
 *
 *   1. `CamlinAvatar.speak()`     — TTS MP3 in a local `new Audio(...)`
 *                                   (Decision Memory's typed-chat replies).
 *   2. `AvatarAudioLayer`         — the `<audio>` element driven by
 *                                   `setAudioSource` (runtime-host speech
 *                                   button, live contextual-chat prompts).
 *   3. `runBrowserTtsFallback()`  — the opt-in `speechSynthesis` fallback.
 *
 * NOT registered, deliberately: CamlinVoice-driven live audio (WebRTC/SIP
 * calls and Visual IVR). There the "utterance" is a real-time RTP stream —
 * the server keeps talking whether or not the browser plays it, so a local
 * pause is a lie (it desyncs, then skips) and a local stop is just mute.
 * During those calls SpeechPlaybackControl instead renders a STOP-ONLY
 * variant (Aug 29 2026) that bypasses this registry entirely and halts the
 * utterance AT THE SOURCE: `avatar:live:stopSpeaking` →
 * LiveModeClient.sendStopSpeaking() → `visual.playback.stop` → the runtime
 * bargeins the voice leg (see the control's header for the full chain).
 * Paths 1–3 cover every case where the avatar is speaking a discrete,
 * locally-played utterance — including live contextual chat, whose prompt
 * MP3s play through path 2.
 *
 * Semantics:
 *   - registering a new playback supersedes the old one (speech paths
 *     already stop their previous utterance before starting the next);
 *   - `pause()` on a source that cannot pause acts as stop — honest
 *     degradation rather than a fake pause;
 *   - all verbs are safe no-ops when nothing is speaking.
 */

export type SpeechPlaybackKind = 'runtime-audio' | 'browser-tts';

export type SpeechPlaybackStatus = 'idle' | 'speaking' | 'paused';

export interface SpeechPlaybackState {
  status: SpeechPlaybackStatus;
  kind: SpeechPlaybackKind | null;
  /** Whether the current source supports true pause/resume. */
  canPause: boolean;
}

export interface SpeechPlaybackSource {
  kind: SpeechPlaybackKind;
  canPause: boolean;
  pause: () => void;
  resume: () => void;
  /** Halt playback AND settle the avatar's talking animation to idle. */
  stop: () => void;
}

export interface SpeechPlaybackHandle {
  /** The utterance finished (or errored) on its own. Idempotent. */
  markEnded: () => void;
  /** True while this registration is still the active playback. */
  isActive: () => boolean;
}

type Listener = (state: SpeechPlaybackState) => void;

const IDLE_STATE: SpeechPlaybackState = Object.freeze({
  status: 'idle' as const,
  kind: null,
  canPause: false,
});

let activeSource: SpeechPlaybackSource | null = null;
let activeStatus: SpeechPlaybackStatus = 'idle';
let snapshot: SpeechPlaybackState = IDLE_STATE;
const listeners = new Set<Listener>();

function publish(): void {
  snapshot = activeSource
    ? { status: activeStatus, kind: activeSource.kind, canPause: activeSource.canPause }
    : IDLE_STATE;
  for (const listener of [...listeners]) {
    try {
      listener(snapshot);
    } catch (error) {
      console.error('[speechPlayback] listener failed:', error);
    }
  }
}

function clearActive(source: SpeechPlaybackSource): void {
  if (activeSource !== source) return;
  activeSource = null;
  activeStatus = 'idle';
  publish();
}

export function registerSpeechPlayback(source: SpeechPlaybackSource): SpeechPlaybackHandle {
  // Supersede, don't stop: each speech path already halts its previous
  // utterance before starting a new one; calling stop() here would settle
  // the avatar to idle right as the new utterance begins.
  activeSource = source;
  activeStatus = 'speaking';
  publish();

  return {
    markEnded: () => clearActive(source),
    isActive: () => activeSource === source,
  };
}

export function getSpeechPlaybackState(): SpeechPlaybackState {
  return snapshot;
}

export function subscribeSpeechPlayback(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function pauseSpeechPlayback(): void {
  if (!activeSource || activeStatus !== 'speaking') return;
  if (!activeSource.canPause) {
    // Honest degradation: a source that can't pause is stopped instead.
    stopSpeechPlayback();
    return;
  }
  activeStatus = 'paused';
  try {
    activeSource.pause();
  } catch (error) {
    console.error('[speechPlayback] pause failed:', error);
  }
  publish();
}

export function resumeSpeechPlayback(): void {
  if (!activeSource || activeStatus !== 'paused') return;
  activeStatus = 'speaking';
  try {
    activeSource.resume();
  } catch (error) {
    console.error('[speechPlayback] resume failed:', error);
  }
  publish();
}

/** Convenience for the pause/resume toggle button. */
export function toggleSpeechPlaybackPause(): void {
  if (activeStatus === 'paused') {
    resumeSpeechPlayback();
  } else {
    pauseSpeechPlayback();
  }
}

export function stopSpeechPlayback(): void {
  if (!activeSource) return;
  const source = activeSource;
  activeSource = null;
  activeStatus = 'idle';
  try {
    source.stop();
  } catch (error) {
    console.error('[speechPlayback] stop failed:', error);
  }
  publish();
}

// ---------------------------------------------------------------------------
// HTMLAudioElement registration helper
// ---------------------------------------------------------------------------

/**
 * Sync cadence for the `avatar:audioTimeSync` dispatches below. Must stay
 * comfortably under the renderer's 250ms extrapolation cap AND its 500ms
 * stale-sync cutoff (Avatar.tsx `getAnimationTime`): while PAUSED the
 * renderer only holds the mouth at the frozen audio position as long as
 * fresh sync events keep arriving — if they stop for 500ms it falls back to
 * its local wall clock and the mouth starts flapping over silence.
 */
const AUDIO_SYNC_INTERVAL_MS = 150;

export interface AudioSpeechPlaybackOptions {
  kind: SpeechPlaybackKind;
  audio: HTMLAudioElement;
  /**
   * Path-specific animation settle for STOP: reset viseme/talking state so
   * the face doesn't keep flapping (the helper only halts the audio).
   */
  onStop: () => void;
  /**
   * Also dispatch `avatar:audioTimeSync` while PLAYING (not just while
   * paused). Pass true for audio elements that don't already publish their
   * clock (CamlinAvatar's local `new Audio`) so the viseme renderer follows
   * the real audio position — that is what makes pause freeze the mouth and
   * resume stay lip-synced. AvatarAudioLayer already publishes at 16ms via
   * react-audio-player's onListen, so it only needs the paused heartbeat.
   */
  dispatchSyncWhilePlaying?: boolean;
}

function dispatchAudioSync(audio: HTMLAudioElement): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('avatar:audioTimeSync', {
    detail: {
      currentTime: audio.currentTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      isPlaying: !audio.paused && !audio.ended,
    },
  }));
}

/**
 * Register a locally-played utterance backed by an HTMLAudioElement.
 * Wires pause/resume/stop to the element, keeps the viseme renderer's
 * external audio clock alive across pauses (frozen-position heartbeat),
 * and marks the registration ended when the element ends or errors.
 */
export function registerAudioSpeechPlayback(options: AudioSpeechPlaybackOptions): SpeechPlaybackHandle {
  const { kind, audio, onStop, dispatchSyncWhilePlaying = false } = options;

  let syncTimer: ReturnType<typeof setInterval> | null = null;

  const stopSyncTimer = () => {
    if (syncTimer !== null) {
      clearInterval(syncTimer);
      syncTimer = null;
    }
  };

  const startSyncTimer = () => {
    if (syncTimer !== null || typeof window === 'undefined') return;
    syncTimer = setInterval(() => {
      if (audio.ended) {
        stopSyncTimer();
        return;
      }
      dispatchAudioSync(audio);
    }, AUDIO_SYNC_INTERVAL_MS);
  };

  const cleanup = () => {
    stopSyncTimer();
    audio.removeEventListener('ended', handleEnded);
    audio.removeEventListener('error', handleEnded);
  };

  const handle = registerSpeechPlayback({
    kind,
    canPause: true,
    pause: () => {
      audio.pause();
      // Frozen-position heartbeat: keep the renderer on the (paused)
      // external audio clock so the mouth holds its pose instead of
      // falling back to wall-clock and flapping over silence.
      dispatchAudioSync(audio);
      startSyncTimer();
    },
    resume: () => {
      if (!dispatchSyncWhilePlaying) {
        // The element's own clock publisher takes over once playing again.
        stopSyncTimer();
      }
      void audio.play().catch((error) => {
        console.error('[speechPlayback] audio resume failed:', error);
      });
    },
    stop: () => {
      cleanup();
      audio.pause();
      onStop();
    },
  });

  function handleEnded(): void {
    cleanup();
    handle.markEnded();
  }

  audio.addEventListener('ended', handleEnded);
  audio.addEventListener('error', handleEnded);

  if (dispatchSyncWhilePlaying) {
    startSyncTimer();
  }

  const wrappedHandle: SpeechPlaybackHandle = {
    markEnded: () => {
      cleanup();
      handle.markEnded();
    },
    isActive: handle.isActive,
  };

  return wrappedHandle;
}
